import { GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import { getI18nRoute } from "src/utils/i18nRoutes";
import i18nRoutes from "src/utils/i18nRoutes/i18nRoutes";
import { ParsedUrlQuery } from "querystring";
import { PathInfo } from "./interface/pathInfo.interface";
import {
  BRAND_GEO_MAP,
  GENDER_CATEGORIES,
  GENDER_TRANSLATIONS,
} from "./constants/utils.constant";
import { Session } from "next-auth";
import { serialize } from "cookie";
import { Gender } from ".";
import { URL_PARAMS } from "src/constants";
import { Locale } from "src/lib/Intl";
import { localeDomainMapV2 } from "src/utils/domains";

export const parsePathInfo = (pathname: string): PathInfo => {
  const pathParams = pathname
    .split("?")[0]
    .split("/")
    .filter((val) => val !== "");

  return {
    pathParams,
    isGenderHomePage: pathParams.length === 1,
    isBrandPage:
      pathParams.length === 3 &&
      Object.values(BRAND_GEO_MAP).includes(pathParams[0]),
    genderParam: pathParams.length === 1 ? pathParams[0] : pathParams[2],
    brandParam: pathParams.length === 3 ? pathParams[1] : undefined,
  };
};

/**
 * Maps a locale string to a corresponding domain.
 * @param locale - The locale string to map.
 * @returns The corresponding domain string.
 */
export const localeToDomain = (locale: string): string => {
  switch (locale) {
    case "en":
      return "com";
    case "en-gb":
      return "co.uk";
    case "nl-be":
      return "be";
    case "it-it":
      return "it";
    case "es-es":
      return "es";
    case "pl-pl":
      return "pl";
    case "fr":
      return "fr";
    case "de":
      return "de";
    default:
      return "nl";
  }
};

export const checkGenderParamForLocale = (
  genderParam: string,
  locale: string
): boolean => {
  // Define gender parameter mappings for different locales
  const genderMappings: Record<string, string[]> = {
    nl: ["dames", "heren", "kinderen"],
    en: ["women", "men", "kids"],
    de: ["damen", "herren", "kinder"],
    fr: ["femme", "homme", "enfant"],
    "it-it": ["donna", "uomo", "bambini"],
    "es-es": ["mujer", "hombre", "ninos"],
    "pl-pl": ["kobiety", "mezczyzni", "dzieci"],
    "en-gb": ["women", "men", "kids"],
  };

  // Extract base locale (e.g., 'nl' from 'nl-nl') - only for it-it, pl-pl, es-es, and nl-be
  const shouldExtractBaseLocale = ["it-it", "pl-pl", "es-es", "nl-be"].includes(
    locale
  );
  let baseLocale = locale;
  if (shouldExtractBaseLocale) {
    baseLocale = locale.split("-")[0];
  }

  // Get valid gender params for the locale
  const validGenderParams =
    genderMappings[locale] ||
    genderMappings[baseLocale] ||
    // eslint-disable-next-line @typescript-eslint/dot-notation
    genderMappings["nl"];

  // Check if the gender parameter is valid for this locale
  return validGenderParams.includes(genderParam.toLowerCase());
};

function getGenderRoute({
  shopPreference,
  locale,
}: {
  shopPreference?: Gender[];
  locale: string;
}): string {
  if (
    locale === "en-gb" &&
    shopPreference &&
    shopPreference.length &&
    shopPreference[0] === "kids"
  ) {
    return "women";
  }

  if (!shopPreference || !shopPreference.length) {
    return "women";
  }

  return shopPreference?.at(0) || "women";
}

// NOTE: Check Next Auth documentation on how to extend session object type
export function getAuthRedirect<T>({
  req,
  res,
  query,
  session,
}: {
  req: GetServerSidePropsContext["req"];
  res: GetServerSidePropsContext["res"];
  query: GetServerSidePropsContext["query"];
  session: Session;
}): GetServerSidePropsResult<T> {
  if (!session?.user?.opt_in && !session?.user?.first_order) {
    res.setHeader("Set-Cookie", serialize("client_opt_in", "1", { path: "/" }));
  }

  const gender = getGenderRoute({
    shopPreference: session.user.shop_preference,
    locale: req.intl?.locale || "en",
  });

  const location = getI18nRoute({
    route: `/${gender}`,
    locale: req?.intl?.locale || "en",
  });

  return {
    redirect: {
      destination: location,
      permanent: false,
    },
  };
}

const getDomainWithoutLocale = () => {
  const isProd = process.env.RELEASE_STAGE === "production";

  return isProd
    ? URL_PARAMS.production.domainWithoutLocale
    : URL_PARAMS.staging.domainWithoutLocale;
};

export const getGenderTranslation = (
  input: string,
  locale: string
): string | undefined => {
  const gender = input.split("?")[0];
  const category = GENDER_CATEGORIES[gender.toLowerCase()];
  return category ? GENDER_TRANSLATIONS[category][locale] : undefined;
};

export const getGenderTranslationForLocale = (
  genderTerm: string,
  targetLocale: string,
  fallbackLocale = "en"
): string => {
  return (
    getGenderTranslation(genderTerm, targetLocale) ||
    getGenderTranslation(genderTerm, fallbackLocale) ||
    genderTerm
  );
};

export const getRedirectionUrl = (
  pathInfo: PathInfo,
  locale: string
): string => {
  /**
   * pathInfo: {
   *   pathParams: [
   *     "marki",
   *     "river-woods",
   *     "dames",
   *   ],
   *   isGenderHomePage: false,
   *   isBrandPage: true,
   *   genderParam: "dames",
   *   brandParam: "river-woods",
   * }
   */

  // pathParams should not be empty and pathParams[0] should be a valid brand slug
  // valid brand slug structure: otrium.com/brand/brandSlug/genderSlug
  const { pathParams, isBrandPage } = pathInfo;
  if (!pathParams.length || !pathParams[0] || pathParams?.length < 3) {
    return "";
  }

  if (isBrandPage && pathInfo.genderParam) {
    // Check if genderParam belongs to current locale/domain
    const genderParamBelongsToCurrentDomain = checkGenderParamForLocale(
      pathInfo.genderParam,
      locale
    );
    const brandGeo = getBrandGeo(locale);
    if (
      !genderParamBelongsToCurrentDomain ||
      pathInfo?.pathParams[0] !== brandGeo
    ) {
      const domain = localeToDomain(locale);
      // Handle case where gender param doesn't belong to current domain
      const redirectionLink = buildHrefLink(pathInfo, [locale, domain]);
      // eslint-disable-next-line no-console
      console.log(
        `Redirecting user to ${redirectionLink} link, due to gender param mismatch`
      );
      return redirectionLink || "";
    }
  }

  return "";
};

export const getBrandGeo = (locale: string): string => {
  return BRAND_GEO_MAP[locale] || "brands";
};

// Build the hreflang path for a given locale and localize for category/outlet/genderless pages
const buildHrefPath = (
  pathInfo: PathInfo,
  [locale, domain]: [string, string],
  hreflangCategorySlugs?: Record<string, string>
): string => {
  if (hreflangCategorySlugs) {
    const categorySlug = hreflangCategorySlugs[locale];
    if (categorySlug) {
      const genderPrefix =
        pathInfo.pathParams.length >= 2
          ? `${getGenderTranslationForLocale(pathInfo.pathParams[0], domain)}/`
          : "";
      return `/${genderPrefix}${categorySlug}`;
    }
    // Locale not in the per-category map — fall through to standard path
    // translation so the ProductListingView PageHead still emits a link for
    // this locale, overriding the app-level PageHead fallback in _app.tsx.
  }

  const currentRoute = `/${pathInfo.pathParams.join("/")}`;
  const routeMatch = getI18nRoute({
    route: currentRoute,
    locale: locale as Locale,
  });
  if (routeMatch !== currentRoute) {
    return routeMatch;
  }

  const [firstSeg, ...restSegs] = pathInfo.pathParams;
  const canonicalGender = GENDER_CATEGORIES[firstSeg?.toLowerCase()];
  if (canonicalGender) {
    const canonicalRoute = `/${canonicalGender}${
      restSegs.length ? `/${restSegs.join("/")}` : ""
    }`;
    const normalizedMatch = getI18nRoute({
      route: canonicalRoute,
      locale: locale as Locale,
    });
    if (normalizedMatch !== canonicalRoute) {
      return normalizedMatch;
    }
  }

  const routes = i18nRoutes as Record<string, Record<string, string | null>>;
  for (const [canonicalKey, translations] of Object.entries(routes)) {
    if (Object.values(translations).includes(currentRoute)) {
      return translations[locale] ?? canonicalKey;
    }
  }

  if (Object.values(BRAND_GEO_MAP).includes(firstSeg) && restSegs.length > 0) {
    const gender = restSegs[1]
      ? `/${getGenderTranslationForLocale(restSegs[1], domain)}`
      : "";
    return `/${getBrandGeo(locale)}/${restSegs[0]}${gender}`;
  }

  if (firstSeg && GENDER_CATEGORIES[firstSeg.toLowerCase()]) {
    const genderForLocale = getGenderTranslationForLocale(firstSeg, domain);
    return `/${genderForLocale}${
      restSegs.length ? `/${restSegs.join("/")}` : ""
    }`;
  }

  // Default case - return original path
  return `/${pathInfo.pathParams.join("/")}`;
};

// Build the hreflang URL for a given path and localize for category/outlet/genderless pages
export const buildHrefLink = (
  pathInfo: PathInfo,
  localeDomain: [string, string],
  hreflangCategorySlugs?: Record<string, string>
): string | null =>
  `${getDomainWithoutLocale()}${localeDomain[1]}${buildHrefPath(
    pathInfo,
    localeDomain,
    hreflangCategorySlugs
  )}`;

// The path as the given locale spells it (/men → /heren on NL), or null when it points off-site.
export const getLocalizedPath = (
  pathname: string,
  locale: string
): string | null => {
  const domain = localeDomainMapV2[locale];
  if (!domain) return null;

  const path = buildHrefPath(parsePathInfo(pathname), [locale, domain]);

  return path.startsWith("/") ? path : null;
};

export const getQueryString = (
  query: ParsedUrlQuery,
  removeKeys: string[] = []
): string => {
  return Object.keys(query)
    .filter((key) => !removeKeys.includes(key))
    .map((key) => {
      const value = query[key];
      if (Array.isArray(value)) {
        return value
          .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
          .join("&");
      }
      return value
        ? `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        : "";
    })
    .filter(Boolean)
    .join("&");
};
